import React from 'react';
import { CalendarBlank, User, Clock, CheckCircle, XCircle, Hourglass } from '@phosphor-icons/react';

interface FeedbackItem {
    id: string | number;
    // camelCase (from analyticsService)
    flowboardId?: string | number;
    employeeName?: string;
    reviewerName?: string;
    empId?: string;
    nomineeId?: string;
    cycleId?: string | number;
    cycleName?: string;
    cycleType?: string;
    nomineeStatus?: string;
    nominatedDate?: string;
    nominationAcceptOrRejectDate?: string;
    reviewDueDate?: string;
    // snake_case (backwards compatibility)
    flowboard_id?: string | number;
    employee_name?: string;
    reviewer_name?: string;
    emp_id?: string;
    nominee_id?: string;
    cycle_id?: string | number;
    cycle_name?: string;
    cycle_type?: string;
    nominee_status?: string;
    nominated_date?: string;
    nomination_accept_or_reject_date?: string;
    review_due_date?: string;
}

interface FeedbackCardProps {
    data: FeedbackItem[] | { data: FeedbackItem[]; metadata?: any };
    options?: {
        show_cycle_type?: boolean;
        show_dates?: boolean;
        max_items?: number;
    };
    variant?: 'reviewer' | 'reviewee';
}

const getNomineeStatusConfig = (status: string) => {
    const s = status?.toLowerCase() || '';
    if (s.includes('complete') || s.includes('submitted')) {
        return { color: 'bg-green-100 text-green-700', icon: CheckCircle, label: 'Completed' };
    }
    if (s.includes('accept') || s.includes('approved')) {
        return { color: 'bg-blue-100 text-blue-700', icon: CheckCircle, label: 'Accepted' };
    }
    if (s.includes('reject') || s.includes('declined')) {
        return { color: 'bg-red-100 text-red-700', icon: XCircle, label: 'Declined' };
    }
    if (s.includes('pending') || s.includes('waiting')) {
        return { color: 'bg-yellow-100 text-yellow-700', icon: Hourglass, label: 'Pending' };
    }
    if (s.includes('progress')) {
        return { color: 'bg-blue-100 text-blue-700', icon: Clock, label: 'In Progress' };
    }
    return { color: 'bg-gray-100 text-gray-700', icon: Hourglass, label: status?.replace(/_/g, ' ') || 'Unknown' };
};

const getCycleTypeColor = (type?: string) => {
    const t = type?.toLowerCase() || '';
    if (t.includes('annual') || t.includes('yearly')) return 'bg-purple-100 text-purple-700';
    if (t.includes('quarterly')) return 'bg-blue-100 text-blue-700';
    if (t.includes('360') || t.includes('peer')) return 'bg-teal-100 text-teal-700';
    if (t.includes('mid')) return 'bg-orange-100 text-orange-700';
    return 'bg-gray-100 text-gray-700';
};

const formatDate = (dateStr?: string) => {
    if (!dateStr) return null;
    try {
        return new Date(dateStr).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    } catch {
        return null;
    }
};

const isOverdue = (dueDateStr?: string): boolean => {
    if (!dueDateStr) return false;
    try {
        const dueDate = new Date(dueDateStr);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return dueDate < today;
    } catch {
        return false;
    }
};

export const FeedbackCard: React.FC<FeedbackCardProps> = ({ data, options = {}, variant = 'reviewer' }) => {
    const {
        show_cycle_type = true,
        show_dates = true,
        max_items = 20
    } = options;

    // Handle case where data might be wrapped in { data: [...] }
    const items: FeedbackItem[] = Array.isArray(data) ? data : (data && 'data' in data) ? data.data : [];
    const displayItems = items.slice(0, max_items);

    if (!displayItems || displayItems.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center h-full text-gray-400 p-4">
                <div className="w-12 h-12 rounded-full bg-gray-50 flex items-center justify-center mb-2">
                    <User size={24} />
                </div>
                <span className="text-sm">
                    {variant === 'reviewer' ? 'No feedback requests' : 'No feedback received'}
                </span>
            </div>
        );
    }

    const getPersonName = (item: FeedbackItem) => {
        if (variant === 'reviewer') {
            return item.employeeName || item.employee_name || 'Unknown Employee';
        }
        return item.reviewerName || item.reviewer_name || 'Unknown Reviewer';
    };

    const getPersonLabel = () => {
        return variant === 'reviewer' ? 'Review for' : 'Feedback from';
    };

    return (
        <div className="flex flex-col gap-3 p-3 h-full overflow-auto bg-gray-50/50">
            {displayItems.map((item, index) => {
                // Support both camelCase and snake_case
                const itemNomineeStatus = item.nomineeStatus || item.nominee_status || '';
                const itemCycleName = item.cycleName || item.cycle_name || '';
                const itemCycleType = item.cycleType || item.cycle_type || '';
                const itemNominatedDate = item.nominatedDate || item.nominated_date;
                const itemReviewDueDate = item.reviewDueDate || item.review_due_date;

                const statusConfig = getNomineeStatusConfig(itemNomineeStatus);
                const StatusIcon = statusConfig.icon;
                const nominatedDate = formatDate(itemNominatedDate);
                const dueDate = formatDate(itemReviewDueDate);
                const overdue = isOverdue(itemReviewDueDate) && !itemNomineeStatus?.toLowerCase().includes('complete');

                return (
                    <div
                        key={`${item.id}-${index}`}
                        className="group relative bg-white rounded-xl border border-gray-200 p-4 shadow-sm hover:shadow-md hover:border-blue-200 transition-all cursor-pointer"
                    >
                        {/* Header: Person Name + Status */}
                        <div className="flex justify-between items-start gap-3 mb-3">
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                    <User size={16} className="text-gray-400 flex-shrink-0" />
                                    <span className="text-xs text-gray-500">{getPersonLabel()}</span>
                                </div>
                                <h3 className="text-sm font-semibold text-gray-900 line-clamp-1 group-hover:text-blue-600 transition-colors">
                                    {getPersonName(item)}
                                </h3>
                                <p className="text-xs text-gray-500 mt-1 line-clamp-1">
                                    {itemCycleName || 'Unknown Cycle'}
                                </p>
                            </div>

                            {/* Status Badge */}
                            <div className="flex-shrink-0">
                                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide ${statusConfig.color}`}>
                                    <StatusIcon size={12} weight="fill" />
                                    {statusConfig.label}
                                </span>
                            </div>
                        </div>

                        {/* Cycle Type Badge */}
                        {show_cycle_type && itemCycleType && (
                            <div className="mb-3">
                                <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide ${getCycleTypeColor(itemCycleType)}`}>
                                    {itemCycleType}
                                </span>
                            </div>
                        )}

                        {/* Footer: Dates */}
                        {show_dates && (
                            <div className="flex items-center justify-between pt-3 border-t border-gray-50 text-xs text-gray-500 flex-wrap gap-2">
                                {nominatedDate && (
                                    <div className="flex items-center gap-1">
                                        <CalendarBlank size={12} />
                                        <span>Nominated {nominatedDate}</span>
                                    </div>
                                )}

                                {dueDate && (
                                    <div className={`flex items-center gap-1 ml-auto ${overdue ? 'text-red-600 font-medium' : ''}`}>
                                        <Clock size={12} />
                                        <span>{overdue ? 'Overdue' : 'Due'} {dueDate}</span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};
